<div class="max-w-2xl mx-auto p-6">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
        <div>
            <h1 class="text-2xl font-semibold text-gray-900">Add Pool Parking Config</h1>
            <p class="text-sm text-gray-600 mt-1">Add a new pool parking configuration to this organization</p>
        </div>
        <button type="button" (click)="onCancel()"
            class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
            <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
            Cancel
        </button>
    </div>

    <!-- Success Message -->
    @if (successMessage) {
    <div class="mb-6">
        <jjames-alert type="success" [dismissible]="true" (dismissed)="onClearSuccess()">
            {{ successMessage }}
        </jjames-alert>
    </div>
    }

    <!-- Error Message -->
    @if (errorMessage) {
    <div class="mb-6">
        <jjames-alert type="error" [dismissible]="true" (dismissed)="onClearError()">
            {{ errorMessage }}
        </jjames-alert>
    </div>
    }

    <!-- Form -->
    <jjames-card>
        <form [formGroup]="configForm" (ngSubmit)="onSubmit()" class="space-y-6 p-6">
            <h3 class="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">Pool Parking Configuration</h3>

            <!-- Info Box -->
            <div class="bg-blue-50 border-l-4 border-blue-400 p-4">
                <div class="flex">
                    <div class="flex-shrink-0">
                        <svg class="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd"
                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                clip-rule="evenodd" />
                        </svg>
                    </div>
                    <div class="ml-3">
                        <p class="text-sm text-blue-700">
                            <strong>Pool Parking</strong> allows an organization to purchase shared parking capacity.
                            Parkers assigned to the pool can park in designated zones up to the concurrent limit.
                        </p>
                    </div>
                </div>
            </div>

            <!-- Name -->
            <div>
                <label for="name" class="block text-sm font-medium text-gray-700 mb-1">Name <span
                        class="text-red-500">*</span></label>
                <input id="name" type="text" formControlName="name"
                    class="w-full px-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    [class.border-red-300]="isFieldInvalid('name')" [class.border-gray-300]="!isFieldInvalid('name')"
                    placeholder="e.g. Corporate Pool A">
                @if (isFieldInvalid('name')) {
                <p class="mt-1 text-sm text-red-600">{{ getFieldError('name') }}</p>
                }
            </div>

            <!-- Description -->
            <div>
                <label for="description" class="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea id="description" formControlName="description" rows="3"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Optional description for this pool configuration"></textarea>
            </div>

            <!-- Max Cars Allowed -->
            <div>
                <label for="maxCarsAllowed" class="block text-sm font-medium text-gray-700 mb-1">Max Cars Allowed <span
                        class="text-red-500">*</span></label>
                <input id="maxCarsAllowed" type="number" formControlName="maxCarsAllowed" min="1"
                    class="w-full px-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    [class.border-red-300]="isFieldInvalid('maxCarsAllowed')"
                    [class.border-gray-300]="!isFieldInvalid('maxCarsAllowed')"
                    placeholder="Maximum concurrent parkers">
                @if (isFieldInvalid('maxCarsAllowed')) {
                <p class="mt-1 text-sm text-red-600">{{ getFieldError('maxCarsAllowed') }}</p>
                }
                <p class="mt-1 text-xs text-gray-500">Maximum number of vehicles that can park concurrently under this
                    pool</p>
            </div>

            <!-- Dates -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label for="validFrom" class="block text-sm font-medium text-gray-700 mb-1">Valid From <span
                            class="text-red-500">*</span></label>
                    <input id="validFrom" type="date" formControlName="validFrom"
                        class="w-full px-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        [class.border-red-300]="isFieldInvalid('validFrom')"
                        [class.border-gray-300]="!isFieldInvalid('validFrom')">
                    @if (isFieldInvalid('validFrom')) {
                    <p class="mt-1 text-sm text-red-600">{{ getFieldError('validFrom') }}</p>
                    }
                </div>
                <div>
                    <label for="validTo" class="block text-sm font-medium text-gray-700 mb-1">Valid To</label>
                    <input id="validTo" type="date" formControlName="validTo"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                    <p class="mt-1 text-xs text-gray-500">Leave blank for no expiration</p>
                </div>
            </div>


            <!-- Overflow Behavior (pool full) -->
            <div>
                <label for="overflowBehavior" class="block text-sm font-medium text-gray-700 mb-1">When the pool is full</label>
                <select id="overflowBehavior" formControlName="overflowBehavior"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                    @for (o of overflowBehaviors; track o.value) { <option [value]="o.value">{{ o.label }}</option> }
                </select>
                @for (o of overflowBehaviors; track o.value) {
                    @if (configForm.get('overflowBehavior')?.value === o.value) {
                        <p class="mt-1 text-xs text-gray-500">{{ o.hint }}</p>
                    }
                }
            </div>

            <!-- Pool Access Window -->
            <app-access-window-field [(value)]="poolWindow"
                enabledLabel="Limit pool access to a daily time window"
                description="When enabled, pool members may only enter during these hours (camera-local). Outside them they are handled as visitors. Leave off for 24/7 pool access."
                defaultStart="06:00" defaultEnd="17:00" />

            <!-- Form Actions -->
            <div class="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
                <jjames-button type="button" variant="secondary" (clicked)="onCancel()" [disabled]="isSubmitting">
                    Cancel
                </jjames-button>
                <jjames-button type="submit" variant="primary" [disabled]="isSubmitting || configForm.invalid"
                    iconLeft="fas fa-save">
                    @if (isSubmitting) {
                    Adding...
                    } @else {
                    Add Pool Config
                    }
                </jjames-button>
            </div>
        </form>
    </jjames-card>
</div>